<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Container } from '@Shelly/shelly-design-system';
	import { openExternalLink } from 'src/helpers/utilities';
	import { energyPriceApi } from '../../api/verivoxApi';
	import { buildVerivoxRedirectUrl } from '../../api/verivoxRedirect';
	import {
		ENERGY_PRICE_CTA_TEXT_COLOR,
		energyPriceColorSchemes,
		type ColorSchemeKey,
	} from '../../utils/colorSchemes';
	import { dismissVerivoxSurface, isVerivoxSurfaceDismissed } from '../../utils/verivoxDismissals';
	import VerivoxOptionsSimple from '../options/VerivoxOptionsSimple.svelte';
	import VerivoxSetupModal from '../../modals/setup/VerivoxSetupModal.svelte';
	import VerivoxAttribution from '../../shared/VerivoxAttribution.svelte';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import { createFunnel } from 'src/helpers/funnel_tracker';
	import {
		VERIVOX_CTA_OPEN_SETUP,
		VERIVOX_SETUP_BANNER_ID,
	} from 'src/helpers/banner_views/banner-ids';
	import { trackBannerCta } from 'src/helpers/banner_views/banner-views-helpers';

	const verivoxFunnel = createFunnel('verivox_setup');

	let isDismissed = false;
	let setupModalOpen = false;
	let hasCachedLocation = false;
	let cachedPostalCode: string | undefined;
	let cachedLocationId: string | null | undefined;
	let cachedTotalUsage: number = 2500;

	const handleDismiss = async () => {
		isDismissed = true;
		verivoxFunnel.trackAbandon('banner');
		await dismissVerivoxSurface('consumption', 'setup');
	};

	const handleOpenSetupModal = () => {
		verivoxFunnel.trackStep('setup_started');
		void trackBannerCta(VERIVOX_SETUP_BANNER_ID, VERIVOX_CTA_OPEN_SETUP, {
			scope: 'per-surface',
			surface: 'consumption',
		});
		setupModalOpen = true;
	};

	const handleExploreMore = () => {
		if (cachedPostalCode && cachedLocationId) {
			const cachedConsumption = energyPriceApi.getCachedConsumption() || 2500;
			const redirectUrl = buildVerivoxRedirectUrl(
				cachedPostalCode,
				cachedLocationId,
				cachedConsumption,
				'5762',
				'2',
			);
			openExternalLink(redirectUrl);
		} else {
			openExternalLink('https://www.verivox.de');
		}
	};

	let wasSetupModalOpen = false;
	$: {
		if (wasSetupModalOpen && !setupModalOpen) {
			const cached = energyPriceApi.getCachedLocation();
			if (cached?.postalCode && cached?.locationId) {
				hasCachedLocation = true;
				cachedPostalCode = cached.postalCode;
				cachedLocationId = cached.locationId;
				cachedTotalUsage = energyPriceApi.getCachedConsumption() || 2500;
			} else {
				hasCachedLocation = false;
				cachedPostalCode = undefined;
				cachedLocationId = undefined;
				cachedTotalUsage = 2500;
			}
		}
		if (wasSetupModalOpen !== setupModalOpen) {
			wasSetupModalOpen = setupModalOpen;
		}
	}

	let currentScheme: ColorSchemeKey = 'scheme1';

	onMount(() => {
		currentScheme = energyPriceColorSchemes.getCurrentScheme();
		isDismissed = isVerivoxSurfaceDismissed('consumption', 'setup');
		if (!isDismissed) {
			verivoxFunnel.trackStep('banner');
			const cached = energyPriceApi.getCachedLocation();
			if (cached?.postalCode && cached?.locationId) {
				hasCachedLocation = true;
				cachedPostalCode = cached.postalCode;
				cachedLocationId = cached.locationId;
				cachedTotalUsage = energyPriceApi.getCachedConsumption() || 2500;
			} else {
				hasCachedLocation = false;
				cachedPostalCode = undefined;
				cachedLocationId = undefined;
				cachedTotalUsage = 2500;
			}
		}
	});
</script>

{#if !isDismissed}
	{#if hasCachedLocation}
		<VerivoxOptionsSimple
			postalCode={cachedPostalCode}
			locationId={cachedLocationId}
			totalUsage={cachedTotalUsage}
			onDismiss={handleDismiss}
		/>
	{:else}
		<TransitionWrapper>
			<Container class="mt-m" padding="0">
				<div
					class="energy-price-suggestion"
					style="background: {energyPriceColorSchemes.schemes[
						currentScheme
					]}; --energy-price-cta-text: {ENERGY_PRICE_CTA_TEXT_COLOR}"
				>
					<div class="suggestion-header">
						<div class="header-content">
							<div class="suggestion-icon">
								<i class="fas fa-bolt"></i>
							</div>
							<div class="text-content">
								<h3 class="suggestion-title">
									{$format('energy_price_setup_prompt_title', {
										default: 'Günstige Stromtarife entdecken',
										locale: 'de',
									})}
								</h3>
								<p class="suggestion-subtitle">
									{$format('energy_price_setup_prompt_message', {
										default:
											'Vergleichen Sie verfügbare Stromtarife in Ihrer Region',
										locale: 'de',
									})}
								</p>
							</div>
							<button class="suggestion-cta" type="button" on:click={handleOpenSetupModal}>
								{$format('button_get_started', {
									default: 'Loslegen',
									locale: 'de',
								})}
								<i class="fas fa-arrow-right"></i>
							</button>
						</div>
					</div>

					<VerivoxAttribution />

					<button
						class="dismiss-button"
						type="button"
						aria-label={$format('button_close', { default: 'Close' })}
						on:click={handleDismiss}
					>
						<i class="fas fa-times"></i>
					</button>
				</div>
			</Container>
		</TransitionWrapper>
	{/if}
{/if}

<VerivoxSetupModal bind:showModal={setupModalOpen} />

<style>
	.energy-price-suggestion {
		border-radius: 12px;
		padding: 16px;
		box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
		position: relative;
		overflow: hidden;
		transition: all 0.3s ease;
	}

	.energy-price-suggestion::before {
		content: '';
		position: absolute;
		top: -50%;
		right: -10%;
		width: 150px;
		height: 150px;
		background: rgba(255, 255, 255, 0.1);
		border-radius: 50%;
		pointer-events: none;
	}

	.suggestion-header {
		position: relative;
		z-index: 1;
		margin-bottom: 12px;
	}

	.header-content {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.suggestion-icon {
		flex-shrink: 0;
		width: 52px;
		height: 52px;
		background: rgba(255, 255, 255, 0.2);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #ffffff;
		font-size: 26px;
		position: relative;
		z-index: 1;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	.text-content {
		flex: 1;
		min-width: 0;
	}

	.suggestion-title {
		font-size: 15px;
		font-weight: 700;
		margin: 0 0 4px 0;
		color: #ffffff;
		line-height: 1.3;
	}

	.suggestion-subtitle {
		font-size: 12px;
		color: rgba(255, 255, 255, 0.9);
		line-height: 1.3;
		margin: 0;
	}

	.suggestion-cta {
		flex-shrink: 0;
		background: rgba(255, 255, 255, 0.95);
		color: var(--energy-price-cta-text, #e64600);
		border: none;
		border-radius: 8px;
		padding: 10px 20px;
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 6px;
		transition: all 0.2s ease;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
		white-space: nowrap;
	}

	.suggestion-cta:hover {
		background: #ffffff;
		transform: translateY(-1px);
		box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
	}

	.suggestion-cta:active {
		transform: translateY(0);
	}

	.suggestion-cta i {
		font-size: 11px;
		transition: transform 0.2s ease;
	}

	.suggestion-cta:hover i {
		transform: translateX(3px);
	}

	.dismiss-button {
		position: absolute;
		top: 8px;
		right: 8px;
		width: 28px;
		height: 28px;
		background: rgba(255, 255, 255, 0.2);
		border: none;
		border-radius: 6px;
		color: #ffffff;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		transition: all 0.2s ease;
		z-index: 2;
		opacity: 0.8;
	}

	.dismiss-button:hover {
		background: rgba(255, 255, 255, 0.3);
		opacity: 1;
	}

	.dismiss-button:active {
		transform: scale(0.95);
	}

	@media (max-width: 768px) {
		.energy-price-suggestion {
			padding: 12px;
		}

		.header-content {
			gap: 10px;
			flex-wrap: wrap;
		}

		.suggestion-icon {
			width: 44px;
			height: 44px;
			font-size: 22px;
		}

		.suggestion-title {
			font-size: 14px;
		}

		.suggestion-subtitle {
			font-size: 11px;
		}

		.suggestion-cta {
			width: 100%;
			justify-content: center;
			padding: 9px 16px;
			font-size: 13px;
			margin-top: 8px;
		}

		.dismiss-button {
			width: 26px;
			height: 26px;
			font-size: 13px;
		}
	}
</style>
